import React, { useState, useEffect, useCallback, useRef } from 'react';
import fileMagasinApi from '../services/file-magasin';
import './FileVirtuelleMagasin.css';

const TYPE_LABEL = { particulier: 'Particulier', professionnel: 'Pro' };
const ANY_VENDEUR = 'Premier vendeur disponible';

function minutesSince(iso, now) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
}
function waitClass(min) {
  if (min >= 15) return 'fm-wait urgent';
  if (min >= 5) return 'fm-wait warn';
  return 'fm-wait';
}
function fmtDuration(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}
function heure(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function FileVirtuelleMagasin({ user, editMode, setHeaderAction }) {
  const [tab, setTab] = useState('file');

  // Pas de bouton d'action dans le header : la file est alimentée par la borne.
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'file' ? 'active' : ''} onClick={() => setTab('file')}>⏳ File d'attente</button>
        <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration</button>
      </div>
      {tab === 'file'
        ? <QueueTab user={user} editMode={editMode} />
        : <ConfigTab user={user} />}
    </div>
  );
}

/* ─────────────────────────────  FILE D'ATTENTE  ───────────────────────────── */
function QueueTab({ user, editMode }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [vendeurFilter, setVendeurFilter] = useState('');
  const [showRecus, setShowRecus] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const isSuperAdmin = user?.role === 'super-admin';

  const load = useCallback(async () => {
    try {
      const r = await fileMagasinApi.getQueue();
      setItems(r.data.items || []);
    } catch { /* silencieux : polling */ }
    finally { setLoaded(true); }
  }, []);

  // Polling 5 s, en pause quand l'onglet navigateur est masqué.
  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 5000);
    return () => clearInterval(id);
  }, [load]);

  // Tick 30 s pour rafraîchir les temps d'attente affichés.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const setStatut = async (id, statut) => {
    try { await fileMagasinApi.setStatut(id, statut); load(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };
  const removeOne = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try { await fileMagasinApi.deleteEntry(id); setSelected(s => { const n = new Set(s); n.delete(id); return n; }); load(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };
  const removeSelected = async () => {
    if (!selected.size || !window.confirm(`Supprimer ${selected.size} entrée(s) ?`)) return;
    try { await Promise.all([...selected].map(id => fileMagasinApi.deleteEntry(id))); setSelected(new Set()); load(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const vendeurs = [...new Map(items.filter(i => i.vendeur_libelle).map(i => [i.vendeur_libelle, i.vendeur_libelle])).values()];
  const matchVendeur = (i) => !vendeurFilter || (vendeurFilter === '__any__' ? !i.vendeur_libelle : i.vendeur_libelle === vendeurFilter);

  const attente = items.filter(i => i.statut === 'en_attente' && matchVendeur(i));
  const enCours = items.filter(i => i.statut === 'en_cours' && matchVendeur(i));
  const recus   = items.filter(i => i.statut === 'termine' && matchVendeur(i));
  const canDelete = isSuperAdmin && editMode;

  const ClientLine = ({ i, actions }) => {
    const wait = minutesSince(i.heure_arrivee, now);
    return (
      <div className="fm-row">
        {canDelete && (
          <input type="checkbox" className="fm-check" checked={selected.has(i.id)} onChange={() => toggleSel(i.id)} />
        )}
        <span className={`fm-type fm-type-${i.type_client}`}>{TYPE_LABEL[i.type_client]}</span>
        <div className="fm-id">
          <div className="fm-nom">{i.nom || i.telephone || '—'}</div>
          <div className="fm-meta">
            {i.motif_libelle && <span className="fm-chip">{i.motif_libelle}</span>}
            <span className="fm-vendeur">{i.vendeur_libelle || ANY_VENDEUR}</span>
          </div>
        </div>
        <div className="fm-time">
          {i.statut === 'en_attente' && <span className={waitClass(wait)}>{fmtDuration(wait)} d'attente</span>}
          {i.statut === 'en_cours' && <span className="fm-since">Reçu à {heure(i.heure_prise_en_charge)}{i.recu_par_nom ? ` · ${i.recu_par_prenom || ''} ${i.recu_par_nom}` : ''}</span>}
          {i.statut === 'termine' && <span className="fm-done">Terminé · arrivé {heure(i.heure_arrivee)}</span>}
        </div>
        <div className="fm-actions">
          {actions}
          {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => removeOne(i.id)}>🗑️</button>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-toolbar">
        <select className={`page-select ${vendeurFilter ? 'active-filter' : ''}`} value={vendeurFilter} onChange={e => setVendeurFilter(e.target.value)}>
          <option value="">Tous les vendeurs</option>
          <option value="__any__">{ANY_VENDEUR}</option>
          {vendeurs.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <span className="fm-counts">{attente.length} en attente · {enCours.length} en cours</span>
        {canDelete && selected.size > 0 && (
          <button className="btn-danger btn-sm" onClick={removeSelected}>Supprimer la sélection ({selected.size})</button>
        )}
      </div>

      {!loaded ? (
        <div className="fm-empty">Chargement…</div>
      ) : (attente.length + enCours.length + recus.length) === 0 ? (
        <div className="fm-empty">Aucun client aujourd'hui. Les arrivées enregistrées en borne apparaîtront ici.</div>
      ) : (
        <>
          <section className="fm-section">
            <h3 className="fm-section-title">⏳ En attente <span>{attente.length}</span></h3>
            {attente.length === 0 ? <p className="fm-section-empty">Personne en attente.</p> :
              attente.map(i => <ClientLine key={i.id} i={i} actions={
                <button className="btn-primary btn-sm" onClick={() => setStatut(i.id, 'en_cours')}>Recevoir →</button>
              } />)}
          </section>

          <section className="fm-section">
            <h3 className="fm-section-title">🟢 En cours <span>{enCours.length}</span></h3>
            {enCours.length === 0 ? <p className="fm-section-empty">Aucune réception en cours.</p> :
              enCours.map(i => <ClientLine key={i.id} i={i} actions={
                <button className="btn-secondary btn-sm" onClick={() => setStatut(i.id, 'termine')}>Terminer ✓</button>
              } />)}
          </section>

          <section className="fm-section">
            <button className="fm-collapse" onClick={() => setShowRecus(v => !v)}>
              {showRecus ? '▾' : '▸'} ✅ Déjà reçus <span>{recus.length}</span>
            </button>
            {showRecus && (recus.length === 0
              ? <p className="fm-section-empty">Aucun client terminé.</p>
              : recus.map(i => <ClientLine key={i.id} i={i} actions={null} />))}
          </section>
        </>
      )}
    </>
  );
}

/* ─────────────────────────────  CONFIGURATION  ───────────────────────────── */
function ConfigTab({ user }) {
  const [vendeurs, setVendeurs] = useState([]);
  const [motifs, setMotifs] = useState([]);
  const [config, setConfig] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [newVendeur, setNewVendeur] = useState('');
  const [newMotif, setNewMotif] = useState('');
  const [newMotifVendeur, setNewMotifVendeur] = useState('');
  const [newMotifPro, setNewMotifPro] = useState('');
  const [newMotifProVendeur, setNewMotifProVendeur] = useState('');
  const settingsRef = useRef({});

  const reload = useCallback(async () => {
    try {
      const [v, m, c] = await Promise.all([
        fileMagasinApi.getVendeurs(),
        fileMagasinApi.getMotifs(),
        fileMagasinApi.getConfig(),
      ]);
      setVendeurs(v.data.vendeurs || []);
      setMotifs(m.data.motifs || []);
      setConfig(c.data.config || null);
      setCanEdit(c.data.can_edit || user?.role === 'super-admin' || user?.role === 'admin');
    } catch { /* */ }
    finally { setLoaded(true); }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const guard = async (fn) => {
    try { await fn(); reload(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };

  const addVendeur = () => { if (newVendeur.trim()) guard(async () => { await fileMagasinApi.createVendeur({ nom: newVendeur.trim() }); setNewVendeur(''); }); };
  const addMotif = () => { if (newMotif.trim()) guard(async () => { await fileMagasinApi.createMotif({ libelle: newMotif.trim(), vendeur_cible_id: newMotifVendeur || null, type_client: 'particulier' }); setNewMotif(''); setNewMotifVendeur(''); }); };
  const addMotifPro = () => { if (newMotifPro.trim()) guard(async () => { await fileMagasinApi.createMotif({ libelle: newMotifPro.trim(), vendeur_cible_id: newMotifProVendeur || null, type_client: 'professionnel' }); setNewMotifPro(''); setNewMotifProVendeur(''); }); };

  const saveSettings = () => guard(async () => {
    await fileMagasinApi.updateConfig({
      magasin_titre: settingsRef.current.titre ?? config?.magasin_titre ?? '',
      magasin_logo_url: settingsRef.current.logo ?? config?.magasin_logo_url ?? '',
      magasin_motif_particulier_actif: settingsRef.current.motifActif ?? config?.magasin_motif_particulier_actif ?? true,
      magasin_motif_professionnel_actif: settingsRef.current.motifProActif ?? config?.magasin_motif_professionnel_actif ?? false,
    });
  });

  if (!loaded) return <div className="fm-empty">Chargement…</div>;
  if (!config) return <div className="fm-empty">Sélectionnez une concession pour configurer la file magasin.</div>;

  const motifsParticulier = motifs.filter(m => m.type_client !== 'professionnel');
  const motifsPro = motifs.filter(m => m.type_client === 'professionnel');

  return (
    <div className="fm-config">
      {!canEdit && <p className="fm-readonly">Lecture seule — vous n'avez pas les droits de modification sur cette page.</p>}

      {/* Vendeurs magasin */}
      <section className="fm-card">
        <h3>👤 Vendeurs magasin</h3>
        <p className="fm-card-hint">Liste tenue au quotidien par le chef de service. « Visible pros » : proposé au choix des clients professionnels en borne.</p>
        {canEdit && (
          <div className="fm-add">
            <input className="fm-input" placeholder="Nom du vendeur" value={newVendeur} onChange={e => setNewVendeur(e.target.value)} onKeyDown={e => e.key === 'Enter' && addVendeur()} />
            <button className="btn-primary btn-sm" onClick={addVendeur}>Ajouter</button>
          </div>
        )}
        {vendeurs.length === 0 ? <p className="fm-section-empty">Aucun vendeur. Ajoutez-en pour permettre le choix en borne (pros).</p> : (
          <table className="fm-mini-table">
            <thead><tr><th>Nom</th><th>Visible pros</th><th>Actif</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {vendeurs.map(v => (
                <tr key={v.id} className={v.actif ? '' : 'fm-inactive'}>
                  <td>{v.nom}</td>
                  <td><input type="checkbox" checked={!!v.dispo_pro} disabled={!canEdit} onChange={() => guard(() => fileMagasinApi.updateVendeur(v.id, { dispo_pro: !v.dispo_pro }))} /></td>
                  <td><input type="checkbox" checked={!!v.actif} disabled={!canEdit} onChange={() => guard(() => fileMagasinApi.updateVendeur(v.id, { actif: !v.actif }))} /></td>
                  {canEdit && <td><div className="action-buttons"><button className="btn-icon delete" title="Supprimer" onClick={() => window.confirm('Supprimer ce vendeur ?') && guard(() => fileMagasinApi.deleteVendeur(v.id))}>🗑️</button></div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Motifs de visite (particuliers) */}
      <section className="fm-card">
        <h3>📝 Motifs de visite (particuliers)</h3>
        <p className="fm-card-hint">Proposés au client particulier en borne. Un vendeur dédié route automatiquement ce motif vers ce vendeur.</p>
        {canEdit && (
          <div className="fm-add">
            <input className="fm-input" placeholder="Libellé (ex. Récupérer une commande)" value={newMotif} onChange={e => setNewMotif(e.target.value)} onKeyDown={e => e.key === 'Enter' && addMotif()} />
            <select className="fm-input" value={newMotifVendeur} onChange={e => setNewMotifVendeur(e.target.value)}>
              <option value="">— Vendeur dédié (optionnel) —</option>
              {vendeurs.filter(v => v.actif).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
            </select>
            <button className="btn-primary btn-sm" onClick={addMotif}>Ajouter</button>
          </div>
        )}
        {motifsParticulier.length === 0 ? <p className="fm-section-empty">Aucun motif. Sans motif, les particuliers passent directement à la saisie de leurs coordonnées.</p> : (
          <table className="fm-mini-table">
            <thead><tr><th>Libellé</th><th>Vendeur dédié</th><th>Actif</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {motifsParticulier.map(m => (
                <tr key={m.id} className={m.actif ? '' : 'fm-inactive'}>
                  <td>{m.libelle}</td>
                  <td>
                    {canEdit ? (
                      <select className="fm-input fm-input-sm" value={m.vendeur_cible_id || ''} onChange={e => guard(() => fileMagasinApi.updateMotif(m.id, { vendeur_cible_id: e.target.value || null }))}>
                        <option value="">File commune</option>
                        {vendeurs.filter(v => v.actif).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
                      </select>
                    ) : (m.vendeur_cible_nom || 'File commune')}
                  </td>
                  <td><input type="checkbox" checked={!!m.actif} disabled={!canEdit} onChange={() => guard(() => fileMagasinApi.updateMotif(m.id, { actif: !m.actif }))} /></td>
                  {canEdit && <td><div className="action-buttons"><button className="btn-icon delete" title="Supprimer" onClick={() => window.confirm('Supprimer ce motif ?') && guard(() => fileMagasinApi.deleteMotif(m.id))}>🗑️</button></div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Motifs de visite (professionnels) */}
      <section className="fm-card">
        <h3>🔧 Motifs de visite (professionnels)</h3>
        <p className="fm-card-hint">Proposés au client professionnel en borne (si activé ci-dessous). Un vendeur dédié route automatiquement ce motif vers ce vendeur.</p>
        {canEdit && (
          <div className="fm-add">
            <input className="fm-input" placeholder="Libellé (ex. Compte pro, devis…)" value={newMotifPro} onChange={e => setNewMotifPro(e.target.value)} onKeyDown={e => e.key === 'Enter' && addMotifPro()} />
            <select className="fm-input" value={newMotifProVendeur} onChange={e => setNewMotifProVendeur(e.target.value)}>
              <option value="">— Vendeur dédié (optionnel) —</option>
              {vendeurs.filter(v => v.actif).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
            </select>
            <button className="btn-primary btn-sm" onClick={addMotifPro}>Ajouter</button>
          </div>
        )}
        {motifsPro.length === 0 ? <p className="fm-section-empty">Aucun motif pro. Sans motif, les professionnels passent au choix du vendeur puis à la saisie de leurs coordonnées.</p> : (
          <table className="fm-mini-table">
            <thead><tr><th>Libellé</th><th>Vendeur dédié</th><th>Actif</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {motifsPro.map(m => (
                <tr key={m.id} className={m.actif ? '' : 'fm-inactive'}>
                  <td>{m.libelle}</td>
                  <td>
                    {canEdit ? (
                      <select className="fm-input fm-input-sm" value={m.vendeur_cible_id || ''} onChange={e => guard(() => fileMagasinApi.updateMotif(m.id, { vendeur_cible_id: e.target.value || null }))}>
                        <option value="">File commune</option>
                        {vendeurs.filter(v => v.actif).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
                      </select>
                    ) : (m.vendeur_cible_nom || 'File commune')}
                  </td>
                  <td><input type="checkbox" checked={!!m.actif} disabled={!canEdit} onChange={() => guard(() => fileMagasinApi.updateMotif(m.id, { actif: !m.actif }))} /></td>
                  {canEdit && <td><div className="action-buttons"><button className="btn-icon delete" title="Supprimer" onClick={() => window.confirm('Supprimer ce motif ?') && guard(() => fileMagasinApi.deleteMotif(m.id))}>🗑️</button></div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Réglages borne */}
      <section className="fm-card">
        <h3>🛎️ Réglages borne</h3>
        <p className="fm-card-hint">URL publique : <code>filevirtuellemagasin.autodom97.fr/{config.slug || '(slug à définir dans Administration › Concessions)'}</code></p>
        <div className="fm-field">
          <label>Titre d'accueil</label>
          <input className="fm-input" defaultValue={config.magasin_titre || ''} disabled={!canEdit} placeholder={`Bienvenue chez ${config.nom || ''}`} onChange={e => { settingsRef.current.titre = e.target.value; }} />
        </div>
        <div className="fm-field">
          <label>Logo (URL)</label>
          <input className="fm-input" defaultValue={config.magasin_logo_url || ''} disabled={!canEdit} placeholder="https://…/logo.png" onChange={e => { settingsRef.current.logo = e.target.value; }} />
        </div>
        <div className="fm-field fm-field-row">
          <label><input type="checkbox" defaultChecked={config.magasin_motif_particulier_actif !== false} disabled={!canEdit} onChange={e => { settingsRef.current.motifActif = e.target.checked; }} /> Demander le motif de visite aux particuliers</label>
        </div>
        <div className="fm-field fm-field-row">
          <label><input type="checkbox" defaultChecked={config.magasin_motif_professionnel_actif === true} disabled={!canEdit} onChange={e => { settingsRef.current.motifProActif = e.target.checked; }} /> Demander le motif de visite aux professionnels</label>
        </div>
        {canEdit && <button className="btn-primary btn-sm" onClick={saveSettings}>Enregistrer les réglages</button>}
      </section>
    </div>
  );
}

export default FileVirtuelleMagasin;
